import type { ComponentProps, MouseEvent } from 'react';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ReclamationAccessPolicy } from 'graphql-ts-types';
import { color, display, fontSize, fontWeight, lineHeight, space } from 'styled-system';

import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import UnderlinedButton from 'common/components/Reclamation/UnderlinedButton';
import { Text } from 'common/components/Text';
import { useLocalization } from 'common/providers/LocaleProvider';
import { getThemeColor } from 'common/theme/helper';
import { hasEmptyPartNumber } from '../../../utils/additional-feedback';
import type { AdditionalInfoTrackingEventData } from '../../../utils/tracking';
import { dispatchAdditionalInfoClickEvent } from '../../../utils/tracking';
import PartNumberField from './PartNumberField';

interface PartNumberAdditionProps {
  values?: string[];
  areInputsDisabled: boolean;
  assemblyInstruction: string;
  shouldValidate?: boolean;
  partNumberPolicy: ReclamationAccessPolicy;
  trackingEventData: AdditionalInfoTrackingEventData;
  onChange: (partNumbers: string[]) => void;
}

const UnstyledContainer = styled.div<any>`
  ${space};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer mb="sp_20" {...props} />
);

const UnstyledLabel = styled.label<any>`
  ${display};
  ${color};
  ${fontSize};
  ${fontWeight};
  ${lineHeight};
  ${space};
`;

const Label = (props: ComponentProps<typeof UnstyledLabel>) => (
  <UnstyledLabel
    display="block"
    color="sys.neutral.text.strong"
    fontSize="fs_16"
    fontWeight="bold"
    lineHeight="lh_20"
    mb="sp_4"
    {...props}
  />
);

const UnstyledDescription = styled(Text)<any>`
  a {
    color: ${getThemeColor('neutral.text.strong')};
  }
`;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription
    fontSize="fs_16"
    mb="sp_16"
    color="sys.neutral.text.default"
    lineHeight="lh_20"
    {...props}
  />
);

const UnstyledStyledUnderlinedButton = styled(UnderlinedButton)``;

const StyledUnderlinedButton = (props: ComponentProps<typeof UnstyledStyledUnderlinedButton>) => (
  <UnstyledStyledUnderlinedButton mb="sp_0" {...props} />
);

const PartNumberAddition = ({
  values,
  areInputsDisabled,
  assemblyInstruction,
  shouldValidate,
  partNumberPolicy,
  trackingEventData,
  onChange,
}: PartNumberAdditionProps) => {
  const [inputValues, setInputValues] = useState(values ?? []);
  const { t, r } = useLocalization();

  useEffect(() => {
    setInputValues(values ?? []);
  }, [values]);

  const handleAddAnotherNumber = () => {
    setInputValues([...inputValues, '']);
  };

  const handleInputRemoval = (index: number) => {
    const remainingValues = inputValues.filter((_, i) => i !== index);
    setInputValues(remainingValues);
    onChange(remainingValues);
  };

  const handleInputChange = (index: number, value: string) => {
    const newInputValues = [...inputValues];
    newInputValues[index] = value;
    setInputValues(newInputValues);
    onChange(newInputValues);
  };

  const handleAssemblyInstructionClick = (event: MouseEvent) => {
    const { nodeName, innerHTML } = event.target as HTMLAnchorElement;

    if (nodeName === 'A') {
      dispatchAdditionalInfoClickEvent({
        ...trackingEventData,
        link_text: innerHTML,
      });
    }
  };

  return (
    <Container data-testid="part-number-addition">
      <Label htmlFor="add-part-number-input-1">
        {t('h24_part_number')}
        {partNumberPolicy !== ReclamationAccessPolicy.Required ? ` (${t('h24_optional')})` : ''}
      </Label>
      {shouldValidate && hasEmptyPartNumber(inputValues) && (
        <ReclamationAlert message={t('h24_part_forced_error')} />
      )}
      <Description
        dangerouslySetInnerHTML={{
          __html: assemblyInstruction
            ? r.t('h24_part_number_description_with_assembly', {
                assemblyInstruction,
              })
            : t('h24_part_number_input_label_description'),
        }}
        onClick={handleAssemblyInstructionClick}
      />
      {inputValues.map((inputValue, i) => (
        <PartNumberField
          key={i}
          value={inputValue}
          index={i}
          name={`add-part-number-input-${i + 1}`}
          id={`add-part-number-input-${i + 1}`}
          isDisabled={areInputsDisabled}
          onChange={handleInputChange}
          onRemove={handleInputRemoval}
        />
      ))}
      <StyledUnderlinedButton onClick={handleAddAnotherNumber}>
        {t('h24_add_another_part_number')}
      </StyledUnderlinedButton>
    </Container>
  );
};

export default PartNumberAddition;
